import { EnvironmentalVariables } from "../../../../enum/EnvironmentalVariables";
import { VisualizationType } from "../../../../enum/VisualizationType";
import i18n from "../../../../i18n";
import { ISerieConfigMap } from "./types/ISerieConfigMap";

export const getPrecipAccumConfigMap = (
  type: VisualizationType,
): Record<string, ISerieConfigMap> => {
  const precipAccumColors: Partial<Record<EnvironmentalVariables, string>> = {
    [EnvironmentalVariables.PRECIP_ACCUM_12H]: "rgba(34, 6, 60, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_24H]: "rgba(106, 19, 184, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_48H]: "rgba(155, 66, 236, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_72H]: "rgba(201, 153, 244, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_96H]: "rgba(255, 100, 169, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_120H]: "rgba(255, 155, 199, 1)",
    [EnvironmentalVariables.LANDSLIDE_PROBABILITY_RISK]:
      "rgba(255, 190, 155, 1)",
  };

  const precipAccumMeasuredDataColors: Partial<
    Record<EnvironmentalVariables, string>
  > = {
    [EnvironmentalVariables.PRECIP_ACCUM_1H]: "rgb(58, 101, 12)",
    [EnvironmentalVariables.PRECIP_ACCUM_3H]: "rgb(36, 162, 36)",
    [EnvironmentalVariables.PRECIP_ACCUM_6H]: "rgb(16, 82, 77)",
    [EnvironmentalVariables.PRECIP_ACCUM_12H]: "rgba(0, 224, 205, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_24H]: "rgba(7, 145, 181, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_48H]: "rgba(43, 204, 247, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_72H]: "rgba(3, 70, 87, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_96H]: "rgba(30, 155, 85, 1)",
    [EnvironmentalVariables.PRECIP_ACCUM_120H]: "rgba(183, 196, 38, 1)",
  };

  const getColor = (
    variable: EnvironmentalVariables,
    type: VisualizationType,
  ) => {
    let colorMap: Record<string, string>;
    switch (type) {
      case VisualizationType.FORECAST:
        colorMap = precipAccumColors;
        break;
      case VisualizationType.SENSOR:
        colorMap = precipAccumMeasuredDataColors;
        break;
      default:
        colorMap = {};
        break;
    }
    return colorMap[variable.toLowerCase()] || "#347ED5";
  };

  const yAxisConfig = {
    title: {
      text: i18n.t(
        "forecastChart.forecastChartPanelMeteogram.precipitationAccumulated",
      ),
      style: {
        color: "#000000",
      },
    },
    min: 0,
    max: 0.6,
    labels: { format: "{value}", style: { color: "#000000" } },
    gridLineWidth: 0,
    tickLength: 0,
  };

  const yAxisConfig1 = {
    title: {
      text: i18n.t("forecastChart.forecastChartPanelMeteogram.probability"),
      style: {
        color: "#000000",
      },
    },
    min: 0,
    max: 100,
    labels: { format: "{value}%", style: { color: "#000000" } },
    opposite: true,
    tickPixelInterval: 55,
  };

  const createPrecipAccumConfig = (
    variable: EnvironmentalVariables,
    zIndex: number,
  ) => ({
    serieConfig: {
      type: "area",
      color: getColor(variable, type),
      fillOpacity: 0.3,
      yAxis: 0,
      zIndex,
      marker: { enabled: false },
      dataLabels: { enabled: false },
      label: { enabled: false },
      lineWidth: 1.5,
      style: { fontSize: "12px" },
      updateYAxis: true,
      updateBiggestMax: false,
    },
    yAxisConfig,
  });

  return {
    ...(type === VisualizationType.SENSOR
      ? {
        [EnvironmentalVariables.PRECIP_ACCUM_1H]: createPrecipAccumConfig(
          EnvironmentalVariables.PRECIP_ACCUM_1H,
          7,
        ),
        [EnvironmentalVariables.PRECIP_ACCUM_3H]: createPrecipAccumConfig(
          EnvironmentalVariables.PRECIP_ACCUM_3H,
          6,
        ),
        [EnvironmentalVariables.PRECIP_ACCUM_6H]: createPrecipAccumConfig(
          EnvironmentalVariables.PRECIP_ACCUM_6H,
          5,
        ),
      }
      : {}),
    [EnvironmentalVariables.PRECIP_ACCUM_12H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_12H,
      6,
    ),
    [EnvironmentalVariables.PRECIP_ACCUM_24H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_24H,
      5,
    ),
    [EnvironmentalVariables.PRECIP_ACCUM_48H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_48H,
      4,
    ),
    [EnvironmentalVariables.PRECIP_ACCUM_72H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_72H,
      3,
    ),
    [EnvironmentalVariables.PRECIP_ACCUM_96H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_96H,
      2,
    ),
    [EnvironmentalVariables.PRECIP_ACCUM_120H]: createPrecipAccumConfig(
      EnvironmentalVariables.PRECIP_ACCUM_120H,
      1,
    ),
    [EnvironmentalVariables.LANDSLIDE_PROBABILITY_RISK]: {
      serieConfig: {
        type: "spline",
        color: getColor(
          EnvironmentalVariables.LANDSLIDE_PROBABILITY_RISK,
          type,
        ),
        yAxis: 1,
        dataLabels: { enabled: false },
        lineWidth: 0.85,
        style: { fontSize: "12px" },
        updateYAxis: true,
        updateBiggestMax: false,
      },
      yAxisConfig: yAxisConfig1,
    },
  };
};
